import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";

// SERVICES
import { deleteDriver, getDrivers } from "../../api/driver";

// APP COMPONENTS
import DriverRow from "./modal/driversModalRow";
import RemoveModal from "../modals/removeModal";
import DriversCreateEdit from "./driversCreateEdit";
import {
  AnimatedModal,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "../modals/AnimatedModal";
import DriversReportDialog from "./DriversReportDialog";

// MATERIAL UI COMPONENTS
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";

// ICONS
import SyncIcon from "@mui/icons-material/Sync";
import ReceiptIcon from "@mui/icons-material/Receipt";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import SearchIcon from "@mui/icons-material/Search";

// Animations
const listItemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: (index) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: index * 0.05,
      duration: 0.3,
    },
  }),
  exit: {
    opacity: 0,
    x: -20,
    transition: { duration: 0.2 },
  },
};

const buttonVariants = {
  hover: { scale: 1.05, transition: { duration: 0.2 } },
  tap: { scale: 0.95, transition: { duration: 0.1 } },
};

export default function DriversModal({ closeModal, setOrderToDriverModal }) {
  const [loading, setLoading] = useState(false);
  const [drivers, setDrivers] = useState([]);
  const [filteredDrivers, setFilteredDrivers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [driverId, setDriverId] = useState(null);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [showCreateDriversModal, setShowCreateDriversModal] = useState(false);
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [removeMessage, setRemoveMessage] = useState("Deletar Entregador?");

  useEffect(() => {
    loadDrivers();
  }, []);

  useEffect(() => {
    if (searchTerm) {
      const filtered = drivers.filter(
        (driver) =>
          driver.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (driver.easy_id && driver.easy_id.toString().includes(searchTerm))
      );
      setFilteredDrivers(filtered);
    } else {
      setFilteredDrivers(drivers);
    }
  }, [searchTerm, drivers]);

  const loadDrivers = async () => {
    try {
      setLoading(true);
      const { data } = await getDrivers();
      data.sort((a, b) => a.name.localeCompare(b.name));
      setDrivers(data);
      setFilteredDrivers(data);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const toggleCreateEditModal = (e, driver_id) => {
    setDriverId(driver_id);
    setShowCreateDriversModal(!showCreateDriversModal);
  };

  const _handleSetDriverOrderModal = (driver) => {
    setOrderToDriverModal && setOrderToDriverModal(driver);
  };

  const toggleDeleteModal = (driver) => {
    console.log("driver", driver);
    setDriverId(driver.driver_id);
    setRemoveMessage(`Deletar Entregador ${driver.name}?`);
    setShowRemoveModal(!showRemoveModal);
  };

  const handleDeleteDriver = async () => {
    try {
      setLoading(true);
      await deleteDriver(driverId);
      await loadDrivers();
      setShowRemoveModal(false);
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  const modalActions = [
    <motion.div
      key="report-button"
      whileHover="hover"
      whileTap="tap"
      variants={buttonVariants}
    >
      <Button
        variant="outlined"
        size="small"
        startIcon={<ReceiptIcon />}
        onClick={() => setShowReportDialog(true)}
        sx={{ mr: 1 }}
      >
        Relatório
      </Button>
    </motion.div>,
    <motion.div
      key="refresh-button"
      whileHover="hover"
      whileTap="tap"
      variants={buttonVariants}
    >
      <IconButton
        color="primary"
        size="small"
        onClick={loadDrivers}
        aria-label="refresh"
        title="Atualizar lista"
      >
        <SyncIcon />
      </IconButton>
    </motion.div>,
  ];

  return (
    <>
      <AnimatedModal
        isOpen={true}
        onClose={closeModal}
        width="450px"
        maxWidth="100%"
        maxHeight="90vh"
      >
        <ModalHeader
          title="Entregadores"
          icon={LocalShippingIcon}
          onClose={closeModal}
          actions={modalActions}
        />

        <ModalContent padding={0}>
          <Box sx={{ p: 2 }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Buscar entregador..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
                sx: { borderRadius: 2 },
              }}
              size="small"
              sx={{ mb: 2 }}
            />
          </Box>

          {loading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "300px",
                flexDirection: "column",
              }}
            >
              <motion.div
                animate={{
                  rotate: 360,
                  transition: {
                    duration: 1.5,
                    repeat: Infinity,
                    ease: "linear",
                  },
                }}
              >
                <LocalShippingIcon
                  color="primary"
                  sx={{ fontSize: 40, mb: 2 }}
                />
              </motion.div>
              <Typography variant="body2" color="text.secondary">
                Carregando entregadores...
              </Typography>
            </Box>
          ) : (
            <Box sx={{ maxHeight: "60vh", overflowY: "auto", pb: 2 }}>
              <AnimatePresence>
                {filteredDrivers.length <= 0 ? (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <Box
                      sx={{
                        p: 4,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        textAlign: "center",
                      }}
                    >
                      <PersonAddIcon
                        color="action"
                        sx={{ fontSize: 60, opacity: 0.3, mb: 2 }}
                      />
                      <Typography
                        variant="h6"
                        color="text.secondary"
                        gutterBottom
                      >
                        Nenhum entregador encontrado
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2 }}
                      >
                        {searchTerm
                          ? "Sua busca não retornou resultados. Tente outros termos."
                          : "Você ainda não cadastrou um entregador. Clique no botão abaixo para adicionar."}
                      </Typography>
                      {!searchTerm && (
                        <motion.div
                          whileHover="hover"
                          whileTap="tap"
                          variants={buttonVariants}
                        >
                          <Button
                            variant="contained"
                            startIcon={<PersonAddIcon />}
                            onClick={toggleCreateEditModal}
                          >
                            Adicionar entregador
                          </Button>
                        </motion.div>
                      )}
                    </Box>
                  </motion.div>
                ) : (
                  <Box>
                    {filteredDrivers.map((driver, index) => (
                      <motion.div
                        key={driver.driver_id}
                        custom={index}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        variants={listItemVariants}
                      >
                        <DriverRow
                          driver={driver}
                          _handleSetDriverOrderModal={
                            _handleSetDriverOrderModal
                          }
                          toggleCreateEditModal={toggleCreateEditModal}
                          deleteDriver={toggleDeleteModal}
                        />
                        <Divider />
                      </motion.div>
                    ))}
                  </Box>
                )}
              </AnimatePresence>
            </Box>
          )}
        </ModalContent>

        <ModalFooter>
          <motion.div
            whileHover="hover"
            whileTap="tap"
            variants={buttonVariants}
            style={{ width: "100%" }}
          >
            <Button
              variant="contained"
              fullWidth
              startIcon={<PersonAddIcon />}
              onClick={toggleCreateEditModal}
              size="large"
            >
              Cadastrar novo entregador
            </Button>
          </motion.div>
        </ModalFooter>
      </AnimatedModal>

      {showCreateDriversModal && (
        <DriversCreateEdit
          id={driverId}
          show={showCreateDriversModal}
          closeModal={toggleCreateEditModal}
          updateDrivers={loadDrivers}
        />
      )}

      <DriversReportDialog
        open={showReportDialog}
        onClose={() => setShowReportDialog(false)}
      />

      <RemoveModal
        title="Atenção"
        show={showRemoveModal}
        toggleModal={toggleDeleteModal}
        onRemove={handleDeleteDriver}
        message={removeMessage}
      />
    </>
  );
}
